/*
  Popup split-test harness, homepage and blog articles.

  Why it exists: at ~1,530 sessions and ~32 quiz starts a week, purchases are far
  too sparse to power a test (a 20% lift needs ~34 weeks). So this measures the
  fastest outcomes the site can resolve: quiz starts, CTA clicks on articles,
  and email signups.

  The first version ran on the homepage only, which took 6% of sessions, so it
  could never be powered. Articles carry most of the traffic, so they are in the
  test now, with their own denominator and CTA-click numerator.

  Arms, a third each:
      a   the Klaviyo popup as it is today
      b   popup held until the visitor scrolls
      c   popup never shown

  How it reports: GA4 needs a registered custom dimension to slice by an event
  parameter, which is admin work and easy to get wrong. So each arm fires its own
  event NAME:

      exp_<name>_<v>_home_view        denominator, homepage pageviews
      exp_<name>_<v>_article_view     denominator, article pageviews
      exp_<name>_<v>_quiz_start       numerator, fired by the quiz sections
      exp_<name>_<v>_article_cta      numerator, article pageviews with a CTA click
      exp_<name>_<v>_article_cta_ours   the same, where the click stayed on our site
      exp_<name>_<v>_email_signup     pageviews with a Klaviyo form submitted
      exp_<name>_b_popup_released     b visitors who scrolled far enough to see it

  The exp_name / exp_variant parameters ride along too, so a proper dimension can
  be added later without invalidating a running test.

  Assignment happens ONLY on the homepage and on articles, because those are the
  denominators. It persists in localStorage so a visitor stays in one arm across
  pages, including the hop to the quiz.

  Deliberately not done here: no stats. This reports counts. Significance is
  computed when the test is read, by stillwater-seo-agent/ab_test_report.py.
*/
(function () {
  'use strict';

  var ARMS = ['a', 'b', 'c'];

  var EXPERIMENT = {
    // Renamed from 'popup' when arm c and the article surface were added. A new
    // name means a new storage key, so every visitor is re-randomized and the
    // old two-arm counts cannot mix with the new ones.
    name: 'popup2',
    enabled: true,
    // Variant b holds the Klaviyo popup until the visitor scrolls. It still
    // opens on Klaviyo's own schedule; we just do not paint it, so Klaviyo's
    // targeting and impression counting are left alone.
    //
    // Scroll rather than a timer, because scrolling is evidence the visitor
    // engaged. Threshold is capped so a tall hero on mobile does not make it
    // unreachable.
    releaseOnScrollPx: 400,
  };

  var STORAGE_KEY = 'sw_exp_' + EXPERIMENT.name;

  function path() {
    return window.location.pathname.replace(/\/+$/, '');
  }

  function isHomepage() {
    var p = path();
    return p === '' || p === '/index';
  }

  // /blogs/<blog>/<handle>, optionally behind a locale prefix. A blog index
  // (/blogs/<blog>) and a tag listing (/blogs/<blog>/tagged/<tag>) have the
  // wrong number of segments, so they do not match.
  function isArticle() {
    return /\/blogs\/[^/]+\/[^/]+$/.test(path());
  }

  // Never run inside the theme editor or a preview: that is Dean looking at his
  // own site, and it would land in the numbers as if it were a customer.
  function isPreview() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function readVariant() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null; // private mode, or storage blocked
    }
  }

  function writeVariant(v) {
    try {
      window.localStorage.setItem(STORAGE_KEY, v);
    } catch (e) {
      /* fall through: the visitor is assigned for this pageview only */
    }
  }

  function assign() {
    var v = readVariant();
    if (ARMS.indexOf(v) !== -1) return v;
    if (!isHomepage() && !isArticle()) return null; // only these pages enroll
    v = ARMS[Math.floor(Math.random() * ARMS.length)];
    writeVariant(v);
    return v;
  }

  // gtag is loaded by the Google & YouTube app tag, not by the theme, so it may
  // not exist yet. Queue and flush rather than defining our own gtag, which
  // would be rewiring someone else's tag.
  var pending = [];
  var flushTries = 0;

  function flush() {
    if (typeof window.gtag !== 'function') {
      if (++flushTries > 40) return; // ~10s, then give up rather than spin
      return window.setTimeout(flush, 250);
    }
    while (pending.length) {
      var e = pending.shift();
      try {
        window.gtag('event', e.name, e.params);
      } catch (err) {
        /* analytics must never break the page */
      }
    }
  }

  function fire(suffix, extra) {
    if (!window.SW_EXP) return;
    var params = {
      event_category: 'experiment',
      exp_name: window.SW_EXP.name,
      exp_variant: window.SW_EXP.variant,
    };
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) params[k] = extra[k];
      }
    }
    pending.push({
      name: 'exp_' + window.SW_EXP.name + '_' + window.SW_EXP.variant + '_' + suffix,
      params: params,
    });
    flush();
  }

  // Numerators that are rates per pageview fire at most once per pageview, so
  // each one is a proportion the report can put an interval on.
  var firedOnce = {};
  function fireOnce(suffix, extra) {
    if (firedOnce[suffix]) return;
    firedOnce[suffix] = true;
    fire(suffix, extra);
  }

  // --- variants b and c: hold the popup ------------------------------------
  // Hide rather than suppress. The form still opens on Klaviyo's own schedule,
  // so its targeting and impression counting are untouched; the visitor simply
  // does not see it.
  //
  // Found the hard way: Klaviyo's `klaviyo-form-*` classes are COMPONENT names
  // (klaviyo-form-richtext, klaviyo-form-button), not a container keyed by form
  // id. A CSS rule on [class*="klaviyo-form-"] hides the footer signup's text
  // and leaves the modal shell on screen. So find overlays structurally: walk up
  // from each form component to the nearest fixed-position ancestor, which is
  // the thing actually covering the page. Every seed is checked, not just the
  // first, because the footer embed can come first in the DOM and has no fixed
  // ancestor, and because an exit-intent form can open after the welcome one.
  function overlayRoots() {
    var seeds = document.querySelectorAll('[class*="klaviyo-form-"]');
    var roots = [];
    for (var i = 0; i < seeds.length; i++) {
      for (var n = seeds[i]; n && n !== document.body; n = n.parentElement) {
        if (n.dataset && n.dataset.swExpHeld) break; // already handled
        var cs = window.getComputedStyle(n);
        if (cs.position === 'fixed' && n.getBoundingClientRect().width > 200) {
          if (roots.indexOf(n) === -1) roots.push(n);
          break;
        }
      }
    }
    return roots;
  }

  // forever: true for arm c, which never releases.
  function holdPopup(thresholdPx, forever) {
    var held = true;
    var hidden = [];

    // b uses opacity, so layout stays intact and Klaviyo's own measurements still
    // hold when the popup is released. c never releases, so it uses display:none,
    // which also takes the invisible modal out of the tab order: a keyboard user
    // must not be able to focus into a form they cannot see.
    var rule = document.createElement('style');
    rule.textContent = forever
      ? '[data-sw-exp-held="1"]{display:none!important}'
      : '[data-sw-exp-held="1"]{opacity:0!important;pointer-events:none!important}';
    (document.head || document.documentElement).appendChild(rule);

    function hide() {
      if (!held) return;
      var roots = overlayRoots();
      for (var i = 0; i < roots.length; i++) {
        // Mark the node and let a stylesheet rule do the hiding. Two reasons,
        // both learned by watching it fail:
        //   1. visibility:hidden does not work. Klaviyo's kl-private-reset-css-*
        //      sets visibility:visible on descendants, and a child overrides an
        //      ancestor's visibility, so the modal paints straight through.
        //   2. An inline style does not survive. Klaviyo rewrites the node's
        //      whole style attribute while running its klaviyo-fadein animation,
        //      wiping anything we set there.
        // An !important rule keyed on the data attribute survives both.
        roots[i].dataset.swExpHeld = '1';
        hidden.push(roots[i]);
      }
      unlockScroll();
    }

    // Klaviyo locks page scrolling while its modal is open, by putting this
    // class on <body>. With the modal hidden, the visitor is left on a page the
    // wheel and touch cannot move, and in b cannot scroll to release the popup.
    // The first version of this test shipped with exactly that bug. So while we
    // are hiding a popup, the lock comes off, and in b it goes back on at release
    // if the popup is still open.
    var LOCK = 'klaviyo-prevent-body-scrolling';
    var removedLock = false;
    function unlockScroll() {
      if (!held || !hidden.length || !document.body || !document.body.classList) return;
      if (document.body.classList.contains(LOCK)) {
        document.body.classList.remove(LOCK);
        removedLock = true;
      }
    }

    // The observer fires on every DOM change, and articles change a lot while
    // their scrollers and product cards hydrate. Batch to one check per frame.
    var scheduled = false;
    function scheduleHide() {
      if (scheduled) return;
      scheduled = true;
      var raf = window.requestAnimationFrame || function (fn) { return window.setTimeout(fn, 16); };
      raf(function () {
        scheduled = false;
        hide();
      });
    }

    function release() {
      if (!held) return;
      held = false;
      var stillOpen = false;
      for (var i = 0; i < hidden.length; i++) {
        hidden[i].removeAttribute('data-sw-exp-held');
        if (document.body.contains(hidden[i])) stillOpen = true;
      }
      hidden.length = 0;
      if (removedLock && stillOpen) document.body.classList.add(LOCK);
      if (observer) observer.disconnect();
      if (lockObserver) lockObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      fire('popup_released');
    }

    // Half a viewport, capped, so this triggers on a phone with a tall hero.
    function threshold() {
      return Math.min(thresholdPx, Math.round((window.innerHeight || 800) * 0.5));
    }

    function onScroll() {
      if ((window.scrollY || window.pageYOffset || 0) >= threshold()) release();
    }

    // Klaviyo announces its own opens; the observer is the backstop for a form
    // injected without one. The open event is handled synchronously so the
    // popup is marked before its first frame.
    window.addEventListener('klaviyoForms', function (e) {
      if (e && e.detail && e.detail.type === 'open') hide();
    });

    var observer = window.MutationObserver
      ? new window.MutationObserver(scheduleHide)
      : null;
    // Watches <body>'s own class only, so it is cheap. Klaviyo may add the
    // scroll lock after the open event, or again on a second form.
    var lockObserver = window.MutationObserver
      ? new window.MutationObserver(unlockScroll)
      : null;
    function observe() {
      if (observer) observer.observe(document.body, { childList: true, subtree: true });
      if (lockObserver) lockObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    if (document.body) {
      observe();
    } else {
      document.addEventListener('DOMContentLoaded', observe);
    }

    if (!forever) {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll(); // someone may land already scrolled, e.g. a restored position
    }
  }

  // --- article CTA clicks ----------------------------------------------------
  // A CTA is a button-styled link in the article: buy buttons, retailer buttons,
  // product-card buttons, the quiz banner. Plain text links in the prose are
  // not CTAs and are left out. The espresso hub buttons are left out too (Dean,
  // 2026-10-21): they are site navigation, not article CTAs, and they have their
  // own espresso_hub_*_click events.
  var CTA_SELECTOR =
    'a.button, a.gear-cta, a.gear-table__buy, a.sw-pc__btn, a[class*="cta"]';

  function isOurs(a) {
    try {
      return new URL(a.href, window.location.href).host === window.location.host;
    } catch (e) {
      return false;
    }
  }

  function watchArticleCtas() {
    document.addEventListener(
      'click',
      function (ev) {
        var a = ev.target && ev.target.closest ? ev.target.closest(CTA_SELECTOR) : null;
        if (!a || !a.closest('.article-template')) return;
        if (a.closest('.espresso-hub-cta')) return;
        var ours = isOurs(a);
        fireOnce('article_cta', { cta_side: ours ? 'ours' : 'partner' });
        if (ours) fireOnce('article_cta_ours');
      },
      // Capture: a partner link opens a new tab and the page can be hidden
      // before a bubbled handler runs.
      true
    );
  }

  // --- email signups ---------------------------------------------------------
  // Any Klaviyo form submitted on any page, once per pageview. Counting every
  // form rather than the popup alone is the point: if hiding the popup moves
  // signups to the footer form or the quiz offer, that is not a loss.
  function watchSignups() {
    window.addEventListener('klaviyoForms', function (e) {
      if (e && e.detail && e.detail.type === 'submit') {
        fireOnce('email_signup', { form_id: String(e.detail.formId || '') });
      }
    });
  }

  // --- run -----------------------------------------------------------------
  if (!EXPERIMENT.enabled || isPreview()) return;

  var variant = assign();
  if (!variant) return; // not in the test: has not landed on an enrolling page yet

  window.SW_EXP = { name: EXPERIMENT.name, variant: variant };

  var home = isHomepage();
  var article = !home && isArticle();

  // Only on the test pages. The quiz results open the welcome form on purpose
  // ("Click to get $15 off"), so the quiz page must never hide it.
  if ((home || article) && variant !== 'a') {
    holdPopup(EXPERIMENT.releaseOnScrollPx, variant === 'c');
  }

  // The denominators. Once per pageview.
  if (home) fire('home_view');
  if (article) {
    fire('article_view');
    watchArticleCtas();
  }

  watchSignups();

  // The quiz numerator is fired from the quiz sections, which call this when
  // quiz_started fires. Exposed rather than inlined so the quiz does not need
  // to know how assignment works.
  window.swExpEvent = fire;
})();
